import React, { useEffect, useState, memo, useCallback, useRef } from 'react';
import i18next from 'i18next';
import Typography from '@mui/material/Typography';
import { sortBy } from 'underscore';
import {
  Grid, Skeleton, Table, TableBody, TableCell, TableHead, TableRow,
  TableContainer, Paper
} from '@mui/material';
import { DragIndicator } from '@mui/icons-material';
import { useDrag, useDrop } from 'react-dnd';
import makeStyles from '@mui/styles/makeStyles';
import TerritoryIcon from '../Common/Icons/Territory.png';
import { localDateYear, localTime, readableDuration, parseTimeStringToDate } from '../Common/timeUtils';
import { azurblau, dispatch_theme, morgengrauen } from '@/config/colors';
import distanceIcon from '../Common/Icons/distance_grey.svg';
import {
  TLStops, TLShipments, RouteSummary
} from '@/config/types/Routes';
import {
  AccessTime, CalendarTodayOutlined,
  DepartureBoardOutlined, LocalShippingOutlined,
} from '@mui/icons-material';
import { useSettings } from '../Settings/SettingsContext';

// Drag-and-drop item type constant
const DND_SHIPMENT_ROW = 'ROLLKARTE_SHIPMENT_ROW';

interface DndShipmentItem {
  type: typeof DND_SHIPMENT_ROW;
  sourceRouteId: string;
  shipmentCustomId: string;
  sourceStopIndex: number;
}

const useStyles = makeStyles(theme => ({
  font: {
    fontFamily: 'Roboto'
  },
  line: {
    borderBottom: '1px solid lightgrey'
  },
  TableWidth: {
    width: '100%'
  },
  td: {
    textAlign: 'left',
    padding: '5px'
  },
  Outerspacing: {
    padding: '0px 10px',
    marginTop: '30px',
  },
  TablerowCell: {
    padding: '16px 9px',
    textAlign: 'left'
  },
  TableHeadclr: {
    background: '#efefef'
  },
  boldCell: {
    fontWeight: 'bold',
    padding: '10px 8px',
    fontSize: '14px',
    textAlign: 'left'
  },
  depotRow: {
    height: 60
  },
  multi: {
    borderLeft: `3px solid ${theme.palette.primary.main} !important`
  },
  pickupBorder: {
    borderLeft: `3px solid ${morgengrauen} !important`
  },
  deliveryBorder: {
    borderLeft: `3px solid ${azurblau} !important`
  },
  logoSection: {
    paddingLeft: '0px',
    paddingTop: '5px'
  },
  dimenionLabel: {
    left: '-4px'
  },
  tourInfo: {
    marginBottom: '15px',
    background: '#ececec'
  },
  tourInfotext: {
    width: '100%',
    marginLeft: 0,
    padding: '0px'
  },
  Touricons: {
    marginRight: '8px',
    fontSize: '23px'
  },
  calendarIcon: {
    marginRight: '8px',
    fontSize: '20px'
  },
  gridHeader: {
    marginBottom: '30px',
    display: 'flex',
    justifyContent: 'space-between'
  },
  infoBlock: {
    display: 'flex',
    alignItems: 'center',
    marginRight: '16px',
    paddingTop: '10px !important',
    paddingBottom: '10px'
  },
  dimenionTableLabel: {
    marginLeft: '8px'
  },
  label: {
    fontWeight: 'bold',
    fontSize: '14px'
  },
  value: {
    marginLeft: '8px',
    fontSize: '14px'
  },
  paperBlock: {
    width: '100%',
    overflow: 'hidden'
  },
  tableHead: {
    background: '#efefef'
  },
  summarySection: {
    marginBottom: '15px',
    background: '#ececec'
  },
  summaryGrid: {
    width: '100%',
    marginLeft: 0,
    padding: '10px 0px'
  },
  distanceIcons: {
    width: '27px',
    height: '27px',
    marginRight: '8px',
  },
  territoryInfo: {
    marginTop: '30px',
    marginBottom: '15px',
    background: '#ececec'
  },
  dragHandle: {
    cursor: 'grab',
    color: '#999',
    padding: '0 4px',
    display: 'flex',
    alignItems: 'center',
    '&:active': {
      cursor: 'grabbing',
    },
  },
  draggingRow: {
    opacity: 0.4,
    background: '#f5f5f5',
  },
  dropTarget: {
    borderTop: '2px solid #ef5927',
  },
  dropZoneRow: {
    height: 8,
    transition: 'height 0.15s ease, background 0.15s ease',
    '&:hover': {
      height: 24,
    },
  },
  dropZoneActive: {
    height: 32,
    background: 'rgba(239, 89, 39, 0.08)',
    borderTop: '2px dashed #ef5927',
  },
}));

interface Route {
  id: string;
  custom_id: string;
  vehicle: string;
  vehicle_name: string;
  start_time: string;
  end_time: string;
  distance: number;
  gross_duration: number;
  deliveries: any[];
  return_time: boolean;
  roundtrip: boolean;
  end_location_id?: string;
  routebase_address_id?: string;
  stops: TLStops[];
  shipments: TLShipments[];
  summary?: RouteSummary;
}

interface RouteTableProps {
  activeCols: string[];
  route: Route;
  logo?: boolean;
  dimension: string;
  isInSummary?: boolean;
  setDimension: (dimension: string) => void;
  isInteractive?: boolean;
  routeId?: string;
  onDropShipment?: (
    sourceRouteId: string,
    shipmentCustomId: string,
    sourceStopIndex: number,
    targetRouteId: string,
    targetStopIndex: number
  ) => void;
}

/**
 * A wrapper that makes a TableRow draggable and a drop target.
 * Used only in interactive mode.
 */
const DraggableShipmentRow: React.FC<{
  children: React.ReactNode;
  routeId: string;
  shipmentCustomId: string;
  stopIndex: number;
  nonDepotIndex: number;
  className: string;
  dataTestId: string;
  onDropShipment: RouteTableProps['onDropShipment'];
}> = ({ children, routeId, shipmentCustomId, stopIndex, nonDepotIndex, className, dataTestId, onDropShipment }) => {
  const ref = useRef<HTMLTableRowElement>(null);

  const [{ isDragging }, drag, preview] = useDrag({
    type: DND_SHIPMENT_ROW,
    item: {
      type: DND_SHIPMENT_ROW,
      sourceRouteId: routeId,
      shipmentCustomId,
      sourceStopIndex: stopIndex,
    } as DndShipmentItem,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isOver }, drop] = useDrop({
    accept: DND_SHIPMENT_ROW,
    drop: (item: DndShipmentItem) => {
      if (onDropShipment) {
        onDropShipment(
          item.sourceRouteId,
          item.shipmentCustomId,
          item.sourceStopIndex,
          routeId,
          nonDepotIndex
        );
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  // Attach both drag preview and drop target to the row
  preview(drop(ref));

  return (
    <TableRow
      ref={ref}
      className={`${className} ${isDragging ? 'rollkarte-dragging' : ''} ${isOver ? 'rollkarte-drop-target' : ''}`}
      data-testid={dataTestId}
      style={{
        opacity: isDragging ? 0.4 : 1,
        background: isDragging ? '#f5f5f5' : isOver ? 'rgba(239, 89, 39, 0.06)' : undefined,
        borderTop: isOver ? '2px solid #ef5927' : undefined,
        cursor: 'grab',
      }}
    >
      <TableCell style={{ padding: '4px 2px', width: 28 }}>
        <span ref={drag as any} style={{ cursor: 'grab', display: 'flex', alignItems: 'center' }}>
          <DragIndicator fontSize="small" style={{ color: '#bbb' }} />
        </span>
      </TableCell>
      {children}
    </TableRow>
  );
};

/**
 * An empty drop zone row at the end of each route table.
 * Allows dropping a shipment at the bottom of the route.
 */
const DropZoneRow: React.FC<{
  routeId: string;
  targetStopIndex: number;
  colSpan: number;
  onDropShipment: RouteTableProps['onDropShipment'];
}> = ({ routeId, targetStopIndex, colSpan, onDropShipment }) => {
  const ref = useRef<HTMLTableRowElement>(null);

  const [{ isOver }, drop] = useDrop({
    accept: DND_SHIPMENT_ROW,
    drop: (item: DndShipmentItem) => {
      if (onDropShipment) {
        onDropShipment(
          item.sourceRouteId,
          item.shipmentCustomId,
          item.sourceStopIndex,
          routeId,
          targetStopIndex
        );
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  drop(ref);

  return (
    <TableRow
      ref={ref}
      style={{
        height: isOver ? 32 : 8,
        background: isOver ? 'rgba(239, 89, 39, 0.08)' : 'transparent',
        borderTop: isOver ? '2px dashed #ef5927' : 'none',
        transition: 'height 0.15s ease, background 0.15s ease',
      }}
      data-testid={`drop-zone-${routeId}`}
    >
      <TableCell
        colSpan={colSpan + 1}
        style={{ padding: 0, border: 'none' }}
      />
    </TableRow>
  );
};

const DropTargetDepotRow: React.FC<{
  children: React.ReactNode;
  routeId: string;
  targetStopIndex: number;
  className: string;
  dataTestId: string;
  onDropShipment: RouteTableProps['onDropShipment'];
}> = ({ children, routeId, targetStopIndex, className, dataTestId, onDropShipment }) => {
  const ref = useRef<HTMLTableRowElement>(null);

  const [{ isOver }, drop] = useDrop({
    accept: DND_SHIPMENT_ROW,
    drop: (item: DndShipmentItem) => {
      if (onDropShipment) {
        onDropShipment(
          item.sourceRouteId,
          item.shipmentCustomId,
          item.sourceStopIndex,
          routeId,
          targetStopIndex
        );
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  drop(ref);

  return (
    <TableRow
      ref={ref}
      className={`${className} ${isOver ? 'rollkarte-drop-target' : ''}`}
      data-testid={dataTestId}
      style={{
        background: isOver ? 'rgba(239, 89, 39, 0.06)' : undefined,
        borderTop: isOver ? '2px solid #ef5927' : undefined,
      }}
    >
      <TableCell style={{ padding: '4px 2px', width: 28 }} />
      {children}
    </TableRow>
  );
};

const RouteTable = memo(({ activeCols, route, logo = true, dimension, isInSummary = false, setDimension, isInteractive = false, routeId, onDropShipment }: RouteTableProps) => {
  const { end_location_id, routebase_address_id } = route;

  const actualStartTime = (() => {
    const firstStop = route.stops?.[0];
    if (firstStop?.departure_time) {
      return firstStop.departure_time;
    }
    return route.start_time;
  })();

  const actualEndTime = (() => {
    const lastStop = route.stops?.[route.stops?.length - 1];
    if (lastStop?.arrival_time) {
      return lastStop.arrival_time;
    }
    return route.end_time;
  })();
  const [multiDeliveries, setMultiDeliveries] = useState({});
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const { settings } = useSettings();
  const dimensions = settings.load_dimensions || [];
  const loadColKey = (backendIdx: number) => `load_${backendIdx}`;

  const loadBackendIndexes: number[] = dimensions
    .map((d: any) => d.backendIndex)
    .filter((x: any) => typeof x === 'number');
  const classes = useStyles();

  const visibleLoadIndexes = loadBackendIndexes.filter((backendIdx) =>
    activeCols.includes(loadColKey(backendIdx))
  );

  const getShipmentType = (shipment: any, pickupRefIds: string[]) => {
    const { type, pickup_ref_id, pickup_id } = shipment;
    if (!type) return '';
    if (type === 'delivery' && pickup_ref_id) return `${i18next.t('common:delivery')} (${i18next.t('common:direct')})`;
    if (type === 'pickup' && pickupRefIds.includes(pickup_id)) return `${i18next.t('common:pickup')} (${i18next.t('common:direct')})`;
    if (type === 'delivery') return i18next.t('common:delivery');
    if (type === 'pickup') return i18next.t('common:pickup');
  };

  const renderRows = useCallback((cols: string[], data: any[]) => {
    const items: any[] = [];
    const pickupRefIds: string[] = [];
    const sorted = sortBy(data, x => x.priority);

    sorted.forEach((item) => {
      if (item?.pickup_ref_id) {
        pickupRefIds.push(item?.pickup_ref_id);
      }
    });

    setLoading(true);
    let nonDepotCounter = 0;
    if (route && data.length) {
      route.stops.forEach((stop, stopIndex) => {
        if (stop?.type === 'depot') return;
        const shipmentIds = [
          ...(stop.shipments_to_load || []),
          ...(stop.shipments_to_unload || [])
        ];
        shipmentIds.forEach((customId) => {
          const shipment = sorted.find(s => s.custom_id === customId);
          if (!shipment) return;
          const address = shipment.location?.address || shipment.delivery_address;
          const customer_name = shipment.customer_name;
          const currentNonDepotIndex = nonDepotCounter++;

          const cells = (
            <>
              {cols.includes('id') && (
                <TableCell className={classes.TablerowCell} data-testid="cell-id">
                  <Typography variant={'body2'}>
                    {shipment.custom_id || shipment.id}
                  </Typography>
                </TableCell>
              )}
              {cols.includes('contactcompany') && (
                <TableCell className={classes.TablerowCell} data-testid="cell-contactcompany">
                  <Typography variant={'body2'}>
                    {customer_name} {address?.firstname} {address?.lastname}
                  </Typography>
                </TableCell>
              )}
              {cols.includes('city') && (
                <TableCell align="left" className={classes.TablerowCell} data-testid="cell-city">
                  <Typography variant={'body2'}>
                    {address?.postal_code} {address?.city}
                  </Typography>
                </TableCell>
              )}
              {cols.includes('address') && (
                <TableCell align="left" className={classes.TablerowCell} data-testid="cell-address">
                  <Typography variant={'body2'}>
                    {address?.street} {address?.housenumber}
                  </Typography>
                </TableCell>
              )}
              {cols.includes('eta') && (
                <TableCell align="left" className={classes.TablerowCell} data-testid="cell-eta">
                  <Typography variant={'body2'}>
                    {stop.arrival_time ? localTime(new Date(stop.arrival_time)) : '--'}
                  </Typography>
                </TableCell>
              )}
              {cols.includes('time_windows') && (() => {
                const tw = shipment.time_windows?.[0];
                let text = '--';
                if (tw && (tw.start || tw.end)) {
                  const toDate = (v: string | null) => v ? (/T|\+|\-00:/.test(v) ? new Date(v) : parseTimeStringToDate(v)) : null;
                  const sDate = toDate(tw.start);
                  const eDate = toDate(tw.end);
                  const s = sDate ? localTime(sDate) : '';
                  const e = eDate ? localTime(eDate) : '';
                  text = s && e ? `${s} -- ${e}` : (s || e || '--');
                }
                return (
                  <TableCell align="left" className={classes.TablerowCell} data-testid="cell-time_windows">
                    <Typography variant={'body2'}>
                      {text}
                    </Typography>
                  </TableCell>
                );
              })()}
              {visibleLoadIndexes.map((backendIdx) => (
                <TableCell
                  key={`cell-load-${backendIdx}`}
                  className={classes.TablerowCell}
                  data-testid={`cell-load-${backendIdx}`}
                >
                  <Typography variant={'body2'}>
                    {shipment.loads?.[backendIdx] !== undefined && shipment.loads?.[backendIdx] !== null
                      ? Math.abs(shipment.loads[backendIdx])
                      : ''}
                  </Typography>
                </TableCell>
              ))}
              {cols.includes('duration') && (
                <TableCell align="left" className={classes.TablerowCell} data-testid="cell-duration">
                  <Typography variant={'body2'}>
                    {(!stop.departure_time || !stop.arrival_time) ? '--' : readableDuration(stop.arrival_time, stop.departure_time)}
                  </Typography>
                </TableCell>
              )}
              {cols.includes('shipment_type') && (
                <TableCell className={classes.TablerowCell} data-testid="cell-shipment_type">
                  <Typography variant={'body2'}>
                    {getShipmentType(shipment, pickupRefIds) || ''}
                  </Typography>
                </TableCell>
              )}
            </>
          );

          if (isInteractive && routeId && onDropShipment) {
            items.push(
              <DraggableShipmentRow
                key={`${stopIndex}-${customId}`}
                routeId={routeId}
                shipmentCustomId={customId}
                stopIndex={stopIndex}
                nonDepotIndex={currentNonDepotIndex}
                className={`${classes.font} ${classes.line} 
                ${shipment.multi ? classes.multi : ''} 
                ${shipment.type === 'pickup' ? classes.pickupBorder : classes.deliveryBorder}`}
                dataTestId={`row-${route.id}-${stopIndex}-${customId}`}
                onDropShipment={onDropShipment}
              >
                {cells}
              </DraggableShipmentRow>
            );
          } else {
            items.push(
              <TableRow key={`${stopIndex}-${customId}`}
                className={`${classes.font} ${classes.line} 
                ${shipment.multi ? classes.multi : ''} 
                ${shipment.type === 'pickup' ? classes.pickupBorder : classes.deliveryBorder}`}
                data-testid={`row-${route.id}-${stopIndex}-${customId}`}>
                {cells}
              </TableRow>
            );
          }
        });
      });
    }

    const lastStop = route.stops[route.stops.length - 1];
    if (lastStop?.type === 'depot' && cols.length) {
      const depotAddress = lastStop.location?.address || lastStop.delivery_address;
      const orderedCols = [
        'id',
        'contactcompany',
        'city',
        'address',
        'eta',
        'time_windows',
        'load',
        'duration',
        'shipment_type'
      ].filter(c => cols.includes(c));

      const depotCells = orderedCols.map((col) => {
        if (col === 'city') {
          return (
            <TableCell key={col} className={classes.TablerowCell} data-testid={`depot-cell-${col}`}>
              <Typography variant="body2">
                {depotAddress
                  ? `${depotAddress.postal_code || ''} ${depotAddress.city || ''}`.trim()
                  : ''}
              </Typography>
            </TableCell>
          );
        }
        if (col === 'load') {
          return visibleLoadIndexes.map((backendIdx) => (
            <TableCell
              key={`depot-cell-load-${backendIdx}`}
              className={classes.TablerowCell}
              data-testid={`depot-cell-load-${backendIdx}`}
            />
          ));
        }
        if (col === 'address') {
          return (
            <TableCell key={col} className={classes.TablerowCell} data-testid={`depot-cell-${col}`}>
              {depotAddress && (depotAddress.street || depotAddress.house_number) ? (
                <>
                  <Typography variant="body2">
                    {`${depotAddress.street || ''} ${depotAddress.house_number || ''}`.trim()}
                  </Typography>
                  <Typography variant="body2">
                    DEPOT
                  </Typography>
                </>
              ) : (
                <Typography variant="body2">
                  DEPOT
                </Typography>
              )}
            </TableCell>
          );
        }

        if (col === 'eta') {
          return (
            <TableCell key={col} className={classes.TablerowCell} data-testid={`depot-cell-${col}`}>
              <Typography variant="body2">
                {lastStop.arrival_time ? localTime(new Date(lastStop.arrival_time as any)) : ''}
              </Typography>
            </TableCell>
          );
        }

        if (col === 'time_windows') {
          return <TableCell key={col} className={classes.TablerowCell} data-testid={`depot-cell-${col}`} />;
        }

        return <TableCell key={col} className={classes.TablerowCell} />;
      });

      if (isInteractive && routeId && onDropShipment) {
        items.push(
          <DropTargetDepotRow
            key="depot-row"
            className={`${classes.font} ${classes.line} ${classes.depotRow}`}
            dataTestId={`depot-row-${route.id}`}
            routeId={routeId}
            targetStopIndex={nonDepotCounter}
            onDropShipment={onDropShipment}
          >
            {depotCells}
          </DropTargetDepotRow>
        );
      } else {
        items.push(
          <TableRow
            key="depot-row"
            className={`${classes.font} ${classes.line} ${classes.depotRow}`}
            data-testid={`depot-row-${route.id}`}
          >
            {depotCells}
          </TableRow>
        );
      }
    } else {
      // If there's no depot row, just append the drop zone at the very end
      if (isInteractive && routeId && onDropShipment) {
        items.push(
          <DropZoneRow
            key="drop-zone"
            routeId={routeId}
            targetStopIndex={nonDepotCounter}
            colSpan={cols.length}
            onDropShipment={onDropShipment}
          />
        );
      }
    }
    setLoading(false);
    setRows(items);
  }, [activeCols, classes, route, dimension, multiDeliveries, isInteractive, routeId, onDropShipment]);

  useEffect(() => {
    if (route) {
      renderRows(activeCols, route.shipments);
    }
  }, [route, renderRows]);

  const sumOfDistance = route.stops.reduce((sum, item) => sum + item.preceding_driving_distance_m, 0);

  const normalize = (s?: string) =>
    (s || '').toLowerCase().normalize('NFKD').replace(/[\s._\-[\]()]/g, '');

  const getUnitForBackendIndex = (backendIdx: number) => {
    const byIndex = dimensions.find(d => d.backendIndex === backendIdx)?.unit;
    if (byIndex) return byIndex;
    const normalize = (s?: string) =>
      (s || '').toLowerCase().normalize('NFKD').replace(/[\s._\-[\]()]/g, '');

    const pick = (test: (n: string) => boolean) =>
      dimensions.find(d => test(normalize(d.dimension)))?.unit;

    if (backendIdx === 2) return pick(n => n.includes('adr') || n.includes('m3') || n.includes('cbm') || n.includes('vol')) || '-';
    if (backendIdx === 1) return pick(n => n.includes('stell') || n.includes('slot') || n.includes('palett') || n.includes('stpl')) || '-';
    return pick(n => n.includes('gewicht') || n.includes('weight') || n.includes('kg')) || '-';
  };

  return (
    <div className={classes.Outerspacing} data-testid={`route-table-${route.id}`}>
      <div className={classes.tourInfo}>
        <Grid container className={classes.tourInfotext} spacing={2}>
          {/* Tour Row */}
          <Grid item className={classes.infoBlock}>
            <LocalShippingOutlined className={classes.Touricons} />
            <Typography variant="body1" className={classes.label}>
              {i18next.t('common:export.Tour')}:
            </Typography>
            <span className={classes.value}>
              {route.vehicle_name}
            </span>
          </Grid>

          {/* Date Row */}
          <Grid item className={classes.infoBlock}>
            <CalendarTodayOutlined className={classes.calendarIcon} />
            <Typography variant="body1" className={classes.label}>
              {i18next.t('common:date')}:
            </Typography>
            <span className={classes.value}>
              {actualStartTime ? localDateYear(new Date(actualStartTime)) : '-'}
            </span>
          </Grid>

          {/* Departure Time Row */}
          <Grid item className={classes.infoBlock}>
            <DepartureBoardOutlined className={classes.Touricons} />
            <Typography variant="body1" className={classes.label}>
              {i18next.t('common:export.departureTime')}:
            </Typography>
            <span className={classes.value}>
              {actualStartTime ? localTime(new Date(actualStartTime)) : ''}
            </span>
          </Grid>

          {/* Distance Row */}
          <Grid item className={classes.infoBlock}>
            <img src={distanceIcon} className={classes.distanceIcons} />
            <Typography variant="body1" className={classes.label}>
              {i18next.t('common:export.distance')}:
            </Typography>
            <span className={classes.value}>
              {Math.round(sumOfDistance / 1000 * 100) / 100} km
            </span>
          </Grid>

          {/* Stay Time Row */}
          <Grid item className={classes.infoBlock}>
            <AccessTime className={classes.Touricons} />
            <Typography variant="body1" className={classes.label}>
              {i18next.t('common:export.stayTimeShort')}:
            </Typography>
            <span className={classes.value}>
              {readableDuration(actualStartTime, actualEndTime)}
            </span>
          </Grid>
        </Grid>
      </div>

      <div className={classes.territoryInfo} data-testid={`route-territories-${route.id}`}>
        <Grid container className={classes.tourInfotext} spacing={2}>
          <Grid item className={classes.infoBlock}>
            <img src={TerritoryIcon} height={25} className={classes.Touricons} />
            <Typography variant="body1" className={classes.label}>
              {i18next.t('common:export.territories')}:
            </Typography>
            <div className={classes.value}>
              {
                ((Array.isArray(route.summary?.vehicle_primary_territory_names) && route.summary.vehicle_primary_territory_names.length > 0) ||
                  (Array.isArray(route.summary?.vehicle_secondary_territory_names) && route.summary.vehicle_secondary_territory_names.length > 0)) ? (
                  <Typography variant="body2">
                    {
                      [
                        ...(route.summary?.vehicle_primary_territory_names || []).map(name => ({ name, type: 'primary' })),
                        ...(route.summary?.vehicle_secondary_territory_names || []).map(name => ({ name, type: 'secondary' }))
                      ]
                        .map((item, index, arr) => (
                          <span
                            key={index}
                            style={{
                              color: item.type === 'primary'
                                ? dispatch_theme.palette.info.main
                                : dispatch_theme.palette.error.light,
                              marginRight: index < arr.length - 1 ? '0' : '0',
                            }}
                          >
                            {item.name}
                            {index < arr.length - 1 && ','}
                          </span>
                        ))
                    }
                  </Typography>
                ) : (
                  '--'
                )
              }
            </div>
          </Grid>
        </Grid>
      </div>
      <Paper sx={{ width: '100%', overflow: 'hidden', '@media print': { overflow: 'visible !important', boxShadow: 'none' } }}>
        <TableContainer sx={{ '@media print': { overflow: 'visible !important' } }}>
          <Table className={classes.TableWidth} data-testid={`route-table-table-${route.id}`}>
            <TableHead className={classes.TableHeadclr} data-testid={`route-table-head-${route.id}`}>
              <TableRow>
                {isInteractive && (
                  <TableCell className={classes.boldCell} style={{ width: 28, padding: '10px 2px' }}></TableCell>
                )}
                {activeCols.includes('id') &&
                  <TableCell className={classes.boldCell} data-testid="th-id">{i18next.t('common:export.id')}
                  </TableCell>}
                {activeCols.includes('contactcompany') &&
                  <TableCell className={classes.boldCell} data-testid="th-contactcompany">{i18next.t('common:export.contact')}
                  </TableCell>}
                {activeCols.includes('city') &&
                  <TableCell className={classes.boldCell} data-testid="th-city">{i18next.t('common:export.city')}
                  </TableCell>}
                {activeCols.includes('address') &&
                  <TableCell className={classes.boldCell} data-testid="th-address">{i18next.t('common:export.address')}
                  </TableCell>}
                {activeCols.includes('eta') &&
                  <TableCell className={classes.boldCell} data-testid="th-eta">{i18next.t('common:export.timewindow')}
                  </TableCell>}
                {activeCols.includes('time_windows') &&
                  <TableCell className={classes.boldCell} data-testid="th-time_windows">{i18next.t('common:routes.timeWindow')}
                  </TableCell>}
                {visibleLoadIndexes.map((backendIdx) => (
                  <TableCell
                    key={`th-load-${backendIdx}`}
                    className={classes.boldCell}
                    data-testid={`th-load-${backendIdx}`}
                  >
                    {i18next.t('common:export.load')}
                    <span className={classes.dimenionTableLabel} data-testid={`th-load-unit-${route.id}-${backendIdx}`}>
                      {getUnitForBackendIndex(backendIdx)}
                    </span>
                  </TableCell>))}
                {activeCols.includes('duration') &&
                  <TableCell className={classes.boldCell} data-testid="th-duration">{i18next.t('common:export.duration')}
                  </TableCell>}
                {activeCols.includes('shipment_type') &&
                  <TableCell className={classes.boldCell} data-testid="th-shipment_type">{i18next.t('common:export.shipmentType')}
                  </TableCell>}
              </TableRow>
            </TableHead>
            <TableBody data-testid={`route-table-body-${route.id}`}>
              {loading ? (
                <TableRow data-testid={`route-table-loading-${route.id}`}>
                  <TableCell colSpan={activeCols.length + (isInteractive ? 1 : 0)}>
                    <Skeleton variant="rectangular" width="100%" height={60} />
                  </TableCell>
                </TableRow>
              ) : (
                rows
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  );
});

export default RouteTable;
